import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Card, CardContent, CardHeader, Container, MenuItem, Select, Tooltip, Typography, Box, IconButton } from '@mui/material';
import {
    DataGrid,
    GridActionsCellItem,
    GridRowEditStopReasons,
    GridRowModes,
    GridToolbarContainer
} from '@mui/x-data-grid';
import { useStoreState } from 'pullstate';
import React, { useEffect, useState } from 'react';
import BackdropLoader from '../../components/BackdropLoader.jsx';
import SearchField from '../../components/SearchField.jsx';
import dataGridStyles from '../../components/styles/dataGridStyles.js';
import { AppStore } from '../../stores/AppStore.js';
import { api } from '../../utils/apiFunctions';
import { updateLocalArrayObjects } from '../../utils/localStorageFunctions.js';

const AccessType = () => {

    const [rows, setRows] = useState([]);
    const [usersName, setUsersName] = useState([]);
    const [rowModesModel, setRowModesModel] = React.useState({});
    const openAlert = useStoreState(AppStore, s => s.snackbar.openAlert);
    const [editableColumnName, setEditableColumnName] = useState(false);
    const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const employees = useStoreState(AppStore, s => s.employees);
    const searchText = useStoreState(AppStore, s => s.searchText);

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id) => {
        setEditableColumnName(false);
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
        setRows(rows.map((row) => (row.id === id ? { ...row, isEditable: false } : row)));
    };

    const handleSaveClick = async (id) => {
        setOpenBackdrop(true);
        const editedRow = rows.find((row) => row.id === id);
        const data = {
            is_lifecycle_admin: editedRow.is_lifecycle_admin === 'Yes' ? true : false,
            is_photo_admin: editedRow.is_photo_admin === 'Yes' ? true : false,
            is_hr_admin: editedRow.is_hr_admin === 'Yes' ? true : false,
        }

        try {
            const response = await api.put(`user/update/${id}`, data)
            if (response.status === 200) {
                openAlert('Access Type updated successfully', 'success')
                updateLocalArrayObjects('employees', id, data);

            } else {
                openAlert('Error updating Access Type', 'error')
            }
        } catch (error) {
            openAlert('Error updating Access Type', 'error')
        } finally {
            setOpenBackdrop(false);
        }
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = async (id) => {

        try {
            const response = await api.delete(`/access-roles/${id}`);
            if (response.status === 200) {
                openAlert('Access Type deleted successfully', 'success')
            } else {
                openAlert('Error deleting Access Type', 'error')
            }
            console.log(response);
            setRows(rows.filter((row) => row.id !== id));
        } catch (error) {
            console.error('Error', error);
        }
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });
        const editedRow = rows.find((row) => row.id === id);
        if (editedRow && editedRow.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = (newRow) => {
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            headerClassName: 'header-class',
            type: 'singleSelect',
            valueOptions: usersName,
            flex: 2,
            editable: editableColumnName,
        },
        {
            field: 'is_lifecycle_admin',
            headerName: 'Admin',
            headerClassName: 'header-class',
            flex: 1,
            type: 'singleSelect',
            valueOptions: ['Yes', 'No'],
            renderCell: (params) => {
                return params.row.is_lifecycle_admin === 'Yes' ? 'Yes' : 'No';
            },
            renderEditCell: (params) => {

                return <Select
                    value={params.value || params.row.is_lifecycle_admin || ''}
                    fullWidth
                    onChange={(event) => {
                        rows.find((row) => row.id === params.id).is_lifecycle_admin = event.target.value;
                        params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
                    }}
                >
                    {['Yes', 'No'].map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>

            },
            editable: true,
            renderHeader: () => (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" >
                    Admin
                  </Typography>
                  <Tooltip title="Highest admin level, access to Company PPB, Reports, and all settings. Users with admin access can make changes to everyone's goals, at any stage of that goal.">
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center'}}>
                        <InfoOutlinedIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                </Box>
            )
        },
        {
            field: 'is_photo_admin',
            headerName: 'Photo Admin',
            headerClassName: 'header-class',
            flex: 1,
            type: 'singleSelect',
            valueOptions: ['Yes', 'No'],
            renderCell: (params) => {
                return params.row.is_photo_admin === 'Yes' ? 'Yes' : 'No';
            },
            renderEditCell: (params) => (
                <Select
                    value={params.value || params.row.is_photo_admin || ''}
                    fullWidth
                    onChange={(event) => {
                        rows.find((row) => row.id === params.id).is_photo_admin = event.target.value;
                        params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
                    }}
                >
                    {['Yes', 'No'].map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
            editable: true,
            renderHeader: () => (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" >
                    Photo Admin
                  </Typography>
                  <Tooltip title="Photo Admin access is very similar to a regular user, with the added feature of changing anyone's user photo.">
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center'}}>
                        <InfoOutlinedIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                </Box>
            )
        },
        {
            field: 'is_hr_admin',
            headerName: 'HR Admin',
            headerClassName: 'header-class',
            flex: 1,
            type: 'singleSelect',
            valueOptions: ['Yes', 'No'],
            renderCell: (params) => {
                return params.row.is_hr_admin === 'Yes' ? 'Yes' : 'No';
            },
            renderEditCell: (params) => (
                <Select
                    value={params.value || params.row.is_hr_admin || ''}
                    fullWidth
                    onChange={(event) => {
                        rows.find((row) => row.id === params.id).is_hr_admin = event.target.value;
                        params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
                    }}
                >
                    {['Yes', 'No'].map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
            editable: true,
            renderHeader: () => (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" >
                    HR Admin                  
                    </Typography>
                  <Tooltip title="This admin level is almost the same as 'Admin', but users with this level will not have access to  the PPB setup and the Access Manager settings.">
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center'}}>
                        <InfoOutlinedIcon fontSize="inherit" />
                    </Box>
                  </Tooltip>
                </Box>
            )
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            headerClassName: 'header-class',
            flex: 1,
            cellClassName: 'actions',
            getActions: (params) => {
                const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            sx={{
                                color: 'inherit',
                            }}
                            onClick={() => {
                                handleCancelClick(params.id)
                                handleSaveClick(params.row.id)
                            }}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(params.id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => handleEditClick(params.id)}
                        color="inherit"
                    />,
                    // <GridActionsCellItem
                    //     icon={<DeleteIcon />}
                    //     label="Delete"
                    //     onClick={() => handleDeleteClick(params.id)}
                    //     color="inherit"
                    // />,
                ];
            },
        },
    ];

    function EditToolbar(props) {
        const { setRows, setRowModesModel } = props;

        const handleClick = () => {
            setEditableColumnName(true);
            setRows((oldRows) => [
                { id: 0, name: '', age: '', role: '', isNew: true },
                ...oldRows
            ]);
            setRowModesModel((oldModel) => ({
                [0]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
                ...oldModel
            }));
        };

        return (
            <GridToolbarContainer>
                <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                    Add record
                </Button>
            </GridToolbarContainer>
        );
    }

    useEffect(() => {
        const getAccessTypes = async () => {
            try {
                // const response = await api.get('/access-roles');
                const usersEtroTeam = employees?.filter(value => value.etro_team);
                const usersName = usersEtroTeam?.map(user => user.full_name).sort();
                const usersFiltered = usersEtroTeam?.filter(user => user.full_name.toLowerCase().includes(searchText.toLowerCase()));
                const users = usersFiltered?.length > 0 ? usersFiltered : usersEtroTeam;
                users?.sort((a, b) => {
                    if (a.is_lifecycle_admin === b.is_lifecycle_admin) {
                        return b.is_photo_admin.toString().localeCompare(a.is_photo_admin.toString());
                    }
                    return b.is_lifecycle_admin.toString().localeCompare(a.is_lifecycle_admin.toString());
                });

                setUsersName(usersName);
                const rows = users?.map((user) => ({
                    id: user.id,
                    name: user.full_name,
                    is_lifecycle_admin: user?.is_lifecycle_admin ? 'Yes' : 'No',
                    is_photo_admin: user?.is_photo_admin ? 'Yes' : 'No',
                    is_hr_admin: user?.is_hr_admin ? 'Yes' : 'No',
                })).sort((a, b) => {
                    if (a.is_lifecycle_admin === b.is_lifecycle_admin) {
                        return a.is_photo_admin === b.is_photo_admin;
                    }
                    return a.is_lifecycle_admin === b.is_lifecycle_admin;
                });
                setRows(rows);
            } catch (error) {
                console.error('Error', error);
            }
        }

        getAccessTypes();
    }, [employees, searchText]);

    return (
        <Container maxWidth="xl" sx={{ mt: 10 }} >
            <BackdropLoader openBackdrop={openBackdrop} />
            <Card style={{ marginTop: '20px' }}>
            <Typography sx={{ paddingTop: '20px' }} variant='h5' align='center'>Access Manager</Typography>
                <div style={{ marginLeft: '10px', display: 'flex', justifyContent: 'flex-start' }}>
                    <SearchField search={searchText} />
                </div>
                <CardContent>
                    <DataGrid
                        sx={dataGridStyles}
                        rows={rows}
                        columns={columns}
                        editMode="row"
                        rowModesModel={rowModesModel}
                        onCellClick={(params) => {
                            if (params.field === "name") {
                                if (params.id !== 0) {
                                    params.isEditable = false;
                                }
                            }
                        }}
                        onRowModesModelChange={handleRowModesModelChange}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                        // slots={{ toolbar: EditToolbar }}
                        hideFooterSelectedRowCount
                        onPaginationModelChange={(params) => setPagination((prev) => ({ ...prev, pageSize: 10, page: params.page }))}
                        paginationModel={pagination}
                        loading={rows?.length === 0}
                        slotProps={{
                            toolbar: { setRows, setRowModesModel },
                            loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
                        }}
                    />
                </CardContent>
            </Card>
        </Container>
    )
}

export default AccessType